import { PlusIcon } from "lucide-react"
import { useEffect, useRef } from "react"
import type { IEnergyPassportProduct } from "@/entities/energy-passport/types.ts"
import {
  type IEnergyPassportProductEditableField,
  type IEnergyPassportQuarter,
  useEnergyPassportStore
} from "@/features/energy-passport/ui/main-table/model/store.ts"
import { Input } from "@/shared/components"
import { Delete02 } from "@/shared/components/ui/icons"
import { useCurrentUser } from "@/shared/hooks/use-current-user.ts"
import { cn } from "@/shared/lib/utils.ts"
import CustomCheckbox from "@/shared/ui/custom-checkbox.tsx"

export const energyPassportProductColumns: {
  field: IEnergyPassportProductEditableField
  inputType?: "number" | "string"
}[] = [
  { field: "count" },
  { field: "count_measure", inputType: "string" },
  { field: "active_energy" },
  { field: "reactive_energy" },
  { field: "thermal_energy" },
  { field: "gas" },
  { field: "mazut" },
  { field: "coal" },
  { field: "gasoline" },
  { field: "diesel" },
  { field: "kerosene" },
  // { field: "oils" },
  { field: "other_petroleum_products" },
  { field: "water" }
]

interface IProps {
  product: IEnergyPassportProduct
  quarterRow: IEnergyPassportQuarter
  index: number
  productLength: number
}

export function EnergyPassportProductRow({ product, quarterRow, productLength, index }: IProps) {
  // Helpers
  const { isCompany } = useCurrentUser()
  const inputRef = useRef<HTMLInputElement>(null)

  // Store
  const { updateProduct, addProduct, removeProduct, invalidProduct, notifyInvalidProduct } = useEnergyPassportStore()

  // Effects
  useEffect(() => {
    if (invalidProduct?.id && product.id === invalidProduct.id) {
      inputRef.current?.focus()
    } else if (product.tempId && product.tempId === invalidProduct?.tempId) {
      inputRef.current?.focus()
    }
  }, [invalidProduct, product.id, product.tempId])

  const isSameProduct = () => {
    if (!invalidProduct) return false
    if (invalidProduct?.id && invalidProduct.id === product.id) {
      return true
    } else if (invalidProduct?.tempId && invalidProduct.tempId === product.tempId) {
      return true
    }
    return false
  }

  return (
    <tr className="child-row remove-bg !py-0 transition">
      <td colSpan={2} className="!py-0 !px-0 !pl-3">
        <div className={`flex items-center gap-4 pl-[10px] ${isCompany ? "dark:bg-zinc-500" : ""}`}>
          <CustomCheckbox
            initialState="false"
            onClick={e => {
              e.stopPropagation()
              e.preventDefault()
            }}
          />
          <Input
            ref={inputRef}
            placeholder="-"
            disabled={isCompany}
            value={product.product_name}
            customError={
              isSameProduct() ? (
                <div className="-translate-x-1/2 absolute top-[-20px] left-1/2 z-20 transform rounded-md bg-pink-500 px-2 py-px text-white">
                  <span className="min-w-[140px] whitespace-nowrap">Введите названия</span>
                  <div className="-translate-x-1/2 absolute bottom-[-4px] left-1/2 h-0 w-0 transform border-x-4 border-x-transparent border-t-4 border-t-pink-500" />
                </div>
              ) : null
            }
            onChange={e => {
              updateProduct(
                quarterRow.quarterNumber,
                {
                  ...product,
                  nameError: !e.target.value,
                  product_name: e.target.value
                },
                "product_name"
              )
              if (isSameProduct()) {
                notifyInvalidProduct(null)
              }
            }}
            className={cn(
              "mx-auto h-[calc(100%-4px)] px-3 ring-blue-400 transition focus:outline-none focus:ring-2 disabled:bg-white disabled:dark:bg-zinc-500",
              isSameProduct() && "ring-pink-500",
              isCompany ? "size-full" : "h-[calc(100%-4px)] w-[calc(100%-4px)!important]"
            )}
            labelProps={{ className: "!rounded-none flex w-full !p-0 border-none" }}
          />
        </div>
      </td>
      {energyPassportProductColumns.map(column => (
        <td key={column.field} className="!py-0 !px-0">
          <Input
            name={column.field}
            value={product[column.field]}
            disabled={isCompany}
            onChange={e =>
              updateProduct(
                quarterRow.quarterNumber,
                {
                  ...product,
                  [column.field]: column.inputType === "string" ? e.target.value : Number.parseInt(e.target.value) || 0
                },
                column.field
              )
            }
            placeholder={column.inputType === "string" ? "" : "0"}
            className={cn(
              "mx-auto px-3 ring-blue-400 transition focus:outline-none focus:ring-2 disabled:bg-white disabled:dark:bg-zinc-500",
              isCompany ? "size-full" : "h-[calc(100%-4px)] w-[calc(100%-4px)!important]"
            )}
            labelProps={{ className: "!rounded-none flex w-full !p-0 border-none" }}
          />
        </td>
      ))}
      <td className="!p-0 pl-4">
        <div className="flex items-center justify-center gap-1 px-2">
          <button
            title="Add"
            type="button"
            onClick={() => addProduct(quarterRow.quarterNumber)}
            className={cn(
              "hidden size-[26px] items-center justify-center rounded-lg bg-zinc-200 ring-blue-400 transition hover:bg-gray-300 focus:outline-none focus:ring-2 active:bg-gray-400 dark:bg-zinc-500",
              index === quarterRow.products.length - 1 && "justify flex"
            )}
          >
            <PlusIcon className="size-[18px]" />
          </button>
          <button
            title="Delete"
            type="button"
            onClick={() => removeProduct(quarterRow.quarterNumber, product.id || product.tempId || "0", !!product.id)}
            className={cn(
              "size-[26px] rounded-lg bg-zinc-200 p-1 ring-pink-600 transition hover:bg-gray-300 focus:outline-none focus:ring-2 active:bg-gray-400 dark:bg-zinc-500",
              productLength === 1 && "hidden"
            )}
          >
            <Delete02 className="size-[18px]" />
          </button>
        </div>
      </td>
    </tr>
  )
}
